import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  ChevronLeft,
  Clock,
  ChevronRight,
  Eye,
  Users,
  Trophy,
  Pause,
  Play,
  RefreshCw,
  Flag,
} from 'lucide-react'

import GlassPanel from '../components/GlassPanel'
import Button from '../components/Button'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { sessionApi } from '../services/sessionApi'
import { Question } from '../types/question'
import { TeamPowerup } from '../types/session'
import { useTheme, themeStyles } from '../context/ThemeContext'
import ParticleBackground from '../components/ParticleBackground'

interface LocationState {
  points: number
  sessionId: string
  currentTurn: string
  team1Score: number
  team2Score: number
  team1Name: string
  team2Name: string
  team1Powerups: TeamPowerup[]
  team2Powerups: TeamPowerup[]
}

type ReportType = 'difficulty' | 'answer' | null;
type DifficultyLevel = 'EASY' | 'MEDIUM' | 'HARD';

const QuestionPage: React.FC = () => {
  const { questionId } = useParams<{ questionId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { theme } = useTheme()
  const currentTheme = themeStyles[theme]

  const [question, setQuestion] = useState<Question | null>(null)
  const [timeLeft, setTimeLeft] = useState(60)
  const [isAnswerRevealed, setIsAnswerRevealed] = useState(false)
  const [isTimerRunning, setIsTimerRunning] = useState(true)
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportType, setReportType] = useState<ReportType>(null)
  const [correctAnswer, setCorrectAnswer] = useState('')
  const [selectedDifficulty, setSelectedDifficulty] = useState<DifficultyLevel | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Extract data from location
  const {
    currentTurn,
    team1Score,
    team2Score,
    team1Name,
    team2Name,
    team1Powerups,
    team2Powerups,
    sessionId,
    points,
  } = location.state as LocationState

  // Local reference for displayed scores
  const [scores, setScores] = useState({ team1: team1Score, team2: team2Score })

  // Check if NO_ANSWER power-up is active for currentTurn
  const hasNoAnswerPowerup = () => {
    if (currentTurn === team1Name) {
      return team1Powerups.some(
        (p) => p.type === 'NO_ANSWER' && p.active && !p.used
      )
    }
    return team2Powerups.some(
      (p) => p.type === 'NO_ANSWER' && p.active && !p.used
    )
  }

  // Determine the next turn
  const getNextTurn = () =>
    currentTurn === team1Name ? team2Name : team1Name

  // Fetch question on mount
  useEffect(() => {
    const fetchQuestion = async () => {
      if (!questionId) return
      try {
        const data = await sessionApi.getQuestion(questionId)
        setQuestion(data)
      } catch (error) {
        console.error('خطأ في جلب السؤال:', error)
      }
    }
    fetchQuestion()
  }, [questionId])

  // Handle timer countdown
  useEffect(() => {
    let interval: NodeJS.Timeout
    if (isTimerRunning && timeLeft > 0) {
      interval = setInterval(() => setTimeLeft((prev) => prev - 1), 1000)
    }
    return () => clearInterval(interval)
  }, [isTimerRunning, timeLeft])

  // Submit an answer
  const handleAnswerSubmit = async (team: string) => {
    if (!sessionId) return
    
    // Calculate the next state locally
    const nextTeam1Score = team === team1Name ? scores.team1 + points : scores.team1
    const nextTeam2Score = team === team2Name ? scores.team2 + points : scores.team2
    
    // Update UI immediately with correct type
    setScores({ team1: nextTeam1Score, team2: nextTeam2Score })
    
    try {
      // Make the API call with powerup state
      const updatedSession = await sessionApi.updateQuestionState(
        sessionId, 
        questionId!, 
        team,
        team1Powerups,
        team2Powerups
      )

      // Navigate with the updated session state
      navigate(`/game/${sessionId}`, {
        state: {
          team1Score: updatedSession.team1score,
          team2Score: updatedSession.team2score,
          currentTurn: getNextTurn(),
          team1Name,
          team2Name,
          team1Powerups: updatedSession.team1Powerups,
          team2Powerups: updatedSession.team2Powerups,
        },
      })
    } catch (error) {
      // Revert optimistic update on error
      setScores({ team1: scores.team1, team2: scores.team2 })
      console.error('خطأ في تحديث حالة السؤال:', error)
    }
  }

  // Handle report submission
  const handleReportSubmit = async () => {
    if (!questionId) return
    setIsSubmitting(true)
    
    try {
      let reportMessage = '';
      
      if (reportType === 'difficulty') {
        reportMessage = `difficulty:${selectedDifficulty}`;
      } else if (reportType === 'answer') {
        reportMessage = `answer:${correctAnswer}`;
      }
      
      // Make API call to report the question
      await sessionApi.reportQuestion(questionId, reportMessage);
      
      setShowReportModal(false);
      setReportType(null);
      setCorrectAnswer('');
      setSelectedDifficulty(null);
      
      // Show success message or notification here if needed
    } catch (error) {
      console.error('خطأ في الإبلاغ عن السؤال:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!question) {
    return (
      <div className={`${currentTheme.text} text-center p-8`}>
        جارٍ تحميل السؤال...
      </div>
    )
  }

  // If NO_ANSWER is active, disable the non-current team
  const isOpponentTeamDisabled = () => (hasNoAnswerPowerup() ? true : false)

  return (
    <div className={`relative h-screen w-full overflow-hidden flex flex-col p-4 md:p-8 bg-gradient-to-br ${currentTheme.background}`}>
      {/* Background elements */}
      <ParticleBackground />

      {/* ---------- SCOREBOARD HEADER ---------- */}
      <GlassPanel className="relative z-10 w-full mb-4 md:mb-8 p-4 backdrop-blur-sm shadow-md">
        <div className="grid grid-cols-3 items-center text-center">
          {/* TEAM 1 */}
          <div className="space-y-1">
            <h2 className={`text-lg md:text-xl font-bold ${currentTheme.text}`}>
              {team1Name}
            </h2>
            <div className="flex items-center justify-center gap-2">
              <Trophy className="text-[#FDCB6E]" />
              <span className={`text-xl md:text-2xl font-bold ${currentTheme.text}`}>
                {scores.team1}
              </span>
            </div>
          </div>

          {/* CURRENT TURN */}
          <motion.div
            className="flex flex-col items-center"
            key={currentTurn}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
          >
            <Users className="text-[#6C5CE7] mb-1 w-6 h-6 md:w-8 md:h-8" />
            <span className="text-sm md:text-base font-semibold bg-gradient-to-r from-[#FF7675] to-[#FDCB6E] bg-clip-text text-transparent">
              دور {currentTurn.toUpperCase()}
            </span>
          </motion.div>

          {/* TEAM 2 */}
          <div className="space-y-1">
            <h2 className={`text-lg md:text-xl font-bold ${currentTheme.text}`}>
              {team2Name}
            </h2>
            <div className="flex items-center justify-center gap-2">
              <Trophy className="text-[#FDCB6E]" />
              <span className={`text-xl md:text-2xl font-bold ${currentTheme.text}`}>
                {scores.team2}
              </span>
            </div>
          </div>
        </div>
      </GlassPanel>

      {/* ---------- MAIN CONTENT ---------- */}
      <GlassPanel className="relative z-10 w-full max-w-4xl mx-auto p-4 md:p-6 flex-1 flex flex-col overflow-hidden backdrop-blur-sm shadow-md">
        {/* Top Bar (Timer, Navigation) */}
        <div className="grid grid-cols-3 items-center mb-4 md:mb-6">
          <Button
            variant="secondary"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 justify-self-start text-sm md:text-base"
            icon={<ChevronLeft className="w-4 h-4" />}
          >
            رجوع
          </Button>

          {/* TIMER CONTROLS */}
          <div className={`flex justify-center items-center gap-4 ${currentTheme.text}`}>
            <span
              className="cursor-pointer"
              onClick={() => setIsTimerRunning(!isTimerRunning)}
            >
              {isTimerRunning ? (
                <Pause className="w-5 h-5 md:w-6 md:h-6" />
              ) : (
                <Play className="w-5 h-5 md:w-6 md:h-6" />
              )}
            </span>

            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 md:w-6 md:h-6" />
              <span className="text-xl md:text-2xl font-bold">{timeLeft}</span>
            </div>

            <span className="cursor-pointer" onClick={() => setTimeLeft(60)}>
              <RefreshCw className="w-5 h-5 md:w-6 md:h-6" />
            </span>
          </div>

          {/* Return to Question if Answer is revealed */}
          {isAnswerRevealed && (
            <div className="flex flex-col items-end gap-2 justify-self-end">
              <Button
                variant="secondary"
                onClick={() => setIsAnswerRevealed(false)}
                className="flex flex-row-reverse items-center gap-2 text-sm md:text-base"
                icon={<ChevronRight className="w-4 h-4" />}
              >
                العودة للسؤال
              </Button>
              
              {/* Report Button - Now under Return button */}
              <button 
                onClick={() => setShowReportModal(true)}
                className="text-[#FF7675] hover:text-[#FF5E57] transition-colors flex items-center gap-1 text-sm"
              >
                <Flag className="w-4 h-4" />
                <span>الإبلاغ عن السؤال</span>
              </button>
            </div>
          )}
        </div>

        {/* QUESTION / ANSWER DISPLAY */}
        <div className="flex-1 flex flex-col items-center justify-between space-y-4 md:space-y-6 overflow-hidden">
          {/* Text */}
          <motion.p
            className={`text-2xl md:text-3xl lg:text-4xl ${currentTheme.text} text-center px-4 leading-relaxed`}
            initial={{ y: 20 }}
            animate={{ y: 0 }}
          >
            {isAnswerRevealed ? question.answer : question.question}
          </motion.p>

          {/* Error Range Display */}
          {!isAnswerRevealed && typeof question.answerRange === 'number' && question.answerRange > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-center gap-2 mt-4"
            >
              <span className="text-3xl md:text-4xl font-bold text-[#FF7675]">-</span>
              <span className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#FF7675] to-[#FDCB6E] bg-clip-text text-transparent">
                {question.answerRange}
              </span>
              <span className="text-3xl md:text-4xl font-bold text-[#FDCB6E]">+</span>
            </motion.div>
          )}

          {/* Image Container */}
          <div className="w-full flex-1 flex items-center justify-center overflow-hidden min-h-0">
            {!isAnswerRevealed && question.questionThumbnailUrl && (
              <motion.img
                src={question.questionThumbnailUrl}
                alt="صورة السؤال"
                className="max-w-full max-h-full object-contain rounded-md"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
              />
            )}
            {isAnswerRevealed && question.answerThumbnailUrl && (
              <motion.img
                src={question.answerThumbnailUrl}
                alt="صورة الإجابة"
                className="max-w-full max-h-full object-contain rounded-md"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
              />
            )}
          </div>

          {/* ACTIONS */}
          <div className="w-full mt-auto">
            {!isAnswerRevealed ? (
              // Reveal answer button
              <div className="flex justify-center">
                <Button
                  variant="primary"
                  onClick={() => {
                    setIsAnswerRevealed(true)
                    setIsTimerRunning(false)
                  }}
                  className="flex items-center justify-center text-base md:text-lg py-3 px-8 max-w-[300px] mx-auto"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Eye className="w-5 h-5 md:w-6 md:h-6" />
                    <span>كشف الإجابة</span>
                  </div>
                </Button>
              </div>
            ) : (
              // Once the answer is revealed
              <div className="flex flex-col w-full gap-4">
                {/* If NO_ANSWER is active */}
                {hasNoAnswerPowerup() && (
                  <div className="text-center text-sm md:text-base text-[#FF7675] font-semibold">
                    فريق {currentTurn} استخدم منع الإجابة
                  </div>
                )}

                <div className="flex flex-col md:flex-row justify-center gap-4 w-full">
                  {/* Team 1 */}
                  <Button
                    variant="primary"
                    onClick={() => handleAnswerSubmit(team1Name)}
                    disabled={
                      currentTurn !== team1Name && isOpponentTeamDisabled()
                    }
                    className="w-full md:min-w-[220px] text-lg md:text-xl py-4 flex items-center justify-center gap-2"
                  >
                    <Users className="w-6 h-6" />
                    {team1Name}
                  </Button>

                  {/* Team 2 */}
                  <Button
                    variant="primary"
                    onClick={() => handleAnswerSubmit(team2Name)}
                    disabled={
                      currentTurn !== team2Name && isOpponentTeamDisabled()
                    }
                    className="w-full md:min-w-[220px] text-lg md:text-xl py-4 flex items-center justify-center gap-2"
                  >
                    <Users className="w-6 h-6" />
                    {team2Name}
                  </Button>
                </div>

                {/* No one answered */}
                <div className="flex justify-center">
                  <Button
                    variant="secondary"
                    onClick={() => handleAnswerSubmit('none')}
                    className="w-full md:min-w-[180px] text-base md:text-lg py-3"
                  >
                    لم يجب أحد
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </GlassPanel>

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <GlassPanel className="w-full max-w-md p-6 overflow-y-auto max-h-[90vh]">
            <div className="flex flex-col gap-4">
              <h2 className={`text-xl font-bold ${currentTheme.text} text-center`}>
                الإبلاغ عن السؤال
              </h2>

              {reportType === null ? (
                <div className="flex flex-col gap-3 mt-2">
                  <Button
                    variant="secondary"
                    onClick={() => setReportType('difficulty')}
                    className="text-base py-3"
                  >
                    الإبلاغ عن مستوى الصعوبة
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setReportType('answer')}
                    className="text-base py-3"
                  >
                    الإبلاغ عن خطأ في الإجابة
                  </Button>
                </div>
              ) : reportType === 'difficulty' ? (
                <div className="flex flex-col gap-3 mt-2">
                  <p className={`text-sm ${currentTheme.text} mb-2`}>اختر مستوى صعوبة السؤال المناسب:</p>
                  <div className="grid grid-cols-3 gap-2">
                    <button 
                      onClick={() => setSelectedDifficulty('EASY')}
                      disabled={question.difficulty === 'EASY'}
                      className={`p-2 rounded-md text-center ${
                        question.difficulty === 'EASY' 
                          ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                          : selectedDifficulty === 'EASY' 
                            ? 'bg-[#55efc4] text-[#2d3436]' 
                            : 'bg-[#6363630f] ' + currentTheme.text
                      }`}
                    >
                      سهل {question.difficulty === 'EASY' && '(الحالي)'}
                    </button>
                    <button 
                      onClick={() => setSelectedDifficulty('MEDIUM')}
                      disabled={question.difficulty === 'MEDIUM'}
                      className={`p-2 rounded-md text-center ${
                        question.difficulty === 'MEDIUM' 
                          ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                          : selectedDifficulty === 'MEDIUM' 
                            ? 'bg-[#fdcb6e] text-[#2d3436]' 
                            : 'bg-[#6363630f] ' + currentTheme.text
                      }`}
                    >
                      متوسط {question.difficulty === 'MEDIUM' && '(الحالي)'}
                    </button>
                    <button 
                      onClick={() => setSelectedDifficulty('HARD')}
                      disabled={question.difficulty === 'HARD'}
                      className={`p-2 rounded-md text-center ${
                        question.difficulty === 'HARD' 
                          ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                          : selectedDifficulty === 'HARD' 
                            ? 'bg-[#ff7675] text-[#2d3436]' 
                            : 'bg-[#6363630f] ' + currentTheme.text
                      }`}
                    >
                      صعب {question.difficulty === 'HARD' && '(الحالي)'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-3 mt-2">
                  <p className={`text-sm ${currentTheme.text} mb-2`}>أدخل الإجابة الصحيحة:</p>
                  <textarea
                    value={correctAnswer}
                    onChange={(e) => setCorrectAnswer(e.target.value)}
                    className="w-full p-3 rounded-md bg-[#1f1f1f] text-white min-h-[80px] max-h-[120px] resize-none"
                    placeholder="أدخل الإجابة الصحيحة هنا..."
                    maxLength={200}
                  />
                </div>
              )}

              <div className="flex gap-2 justify-between mt-4">
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (reportType === null) {
                      setShowReportModal(false);
                    } else {
                      setReportType(null);
                      setCorrectAnswer('');
                      setSelectedDifficulty(null);
                    }
                  }}
                  className="px-4 py-2"
                >
                  {reportType === null ? 'إلغاء' : 'رجوع'}
                </Button>
                
                {reportType !== null && (
                  <Button
                    variant="primary"
                    onClick={handleReportSubmit}
                    disabled={
                      isSubmitting || 
                      (reportType === 'difficulty' && !selectedDifficulty) || 
                      (reportType === 'answer' && !correctAnswer.trim())
                    }
                    className="px-4 py-2"
                  >
                    {isSubmitting ? 'جاري الإرسال...' : 'إرسال'}
                  </Button>
                )}
              </div>
            </div>
          </GlassPanel>
        </div>
      )}
    </div>
  )
}

export default QuestionPage
